import { ChangeDetectionStrategy, Component, computed, inject, OnInit, ViewEncapsulation, input } from '@angular/core';
import { ITodoSection } from '@fi-app/fi-onboarding/fi-todo/fi-todo';
import { ObjectTypes } from '@fi-shared/types';
import { FiTodoService } from '@fi-app/fi-onboarding/fi-todo/fi-todo.service';
import { FiTodoHelperService, FiTodoListEditorSections } from '@fi-app/fi-onboarding/fi-todo/fi-todo-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiTodoListStoreService } from './fi-todo-list-editor-store.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IContentLanguageLabelModel } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { ISectionCardData } from '@fi-shared/fi-section-card/fi-section-card';
import { ValidationErrors } from '@angular/forms';
import {
    IAccessRightCoreFilter,
    IAccessRightFilterCollection,
    AccessRightFilterCollectionModel,
} from '@fi-app/shared/models/access-right-filter-collection.model';
import { FiAccessRightHelperService } from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { isEqual } from '@fi-app/shared/utils/is-equal';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';

@Component({
    selector: 'fi-todo-edit',
    templateUrl: './fi-todo-edit.component.html',
    styleUrl: './fi-todo-edit.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-todo-edit' },
    standalone: false,
})
export class FiTodoEditComponent implements OnInit {
    private fiTodoListStoreService = inject(FiTodoListStoreService);
    private appTextService = inject(AppTextService);
    private fiStateService = inject(FiStateService);
    private fiTodoHelperService = inject(FiTodoHelperService);
    private fiTodoService = inject(FiTodoService);
    private fiAccessRightHelperService = inject(FiAccessRightHelperService);
    private saveChangesWarningService = inject(SaveChangesWarningService);

    public todoListId = input<number>();

    protected accessRightFilterCollection = computed<IAccessRightFilterCollection>(() => {
        return this.fiTodoListStoreService.todoListAccessRightFilterCollection();
    });

    protected selectedLanguageId = computed(() => this.fiTodoListStoreService.selectedLanguageId());
    protected todoListItem = computed(() => this.fiTodoListStoreService.currentTodoListData());
    private isCopyMode = computed(() => !!this.todoListId() && !this.todoListItem().Id);

    protected todoListEditorTitle = computed(() => {
        return this.fiTodoHelperService.getTodoListHeaderText(this.todoListItem().Name, this.isCopyMode());
    });

    protected sectionAdditionalText = computed(() => {
        const todoList = this.todoListItem();
        return {
            [FiTodoListEditorSections.TodoItems]: this.fiTodoHelperService.getTodoItemsSectionDescription(
                todoList.Sections,
            ),
            [FiTodoListEditorSections.SharingSettings]: this.fiTodoHelperService.getSharingSettingsSectionDescription(
                this.accessRightFilterCollection(),
            ),
        };
    });

    public ngOnInit(): void {
        this.fiTodoListStoreService.setCurrentTodoListItem(null);
        this.fiTodoListStoreService.setTodoListSections(this.todoListItem()?.Sections);
        this.startWarningChanges();
    }

    private startWarningChanges(): void {
        this.saveChangesWarningService.start((isReceivingInitialObject) =>
            isReceivingInitialObject ? this.fiTodoListStoreService.initialTodoListData() : this.todoListItem(),
        );
    }

    protected setSelectedLanguageId(value: number): void {
        this.fiTodoListStoreService.setSelectedLanguageId(value);
    }

    protected onTodoListEditItemMediaBankFileReferencesChange(
        mediaBankFileReferences: IMediaBankFileReference[],
    ): void {
        this.fiTodoListStoreService.updateTodoListData((current) => ({
            ...current,
            MediaBankFileReferences: mediaBankFileReferences,
        }));
    }
    protected onTodoListEditItemDescriptionsChange(descriptions: IContentLanguageLabelModel[]): void {
        this.fiTodoListStoreService.updateTodoListData((current) => ({
            ...current,
            Descriptions: descriptions,
        }));
    }

    protected onTodoListEditItemNamesChange(names: IContentLanguageLabelModel[]): void {
        this.fiTodoListStoreService.updateTodoListData((current) => ({ ...current, Names: names }));
    }

    protected navigateToTodoListList(): void {
        this.fiStateService.checkReturnToState(() => {
            this.fiStateService.go(FiStateNames.Onboarding.Todo.List, { filterType: SideListOptionType.SharedWithMe });
        });
    }

    protected save(): void {
        this.fiTodoService.saveTodoList(this.todoListItem()).then(() => {
            this.saveChangesWarningService.stop();
            this.navigateToTodoListList();
        });
    }

    protected onValidateError(errors: ValidationErrors): void {
        this.fiTodoHelperService.showAllItemsMandatoryErrorDialog();
    }

    private validateCheckpointsFn(todoSections: ITodoSection[]): ValidationErrors {
        return {
            checkpointsRequired: !!todoSections.filter((todoSection) => !todoSection.Items.length).length,
        };
    }

    private validateSharingSettingsFn(
        sharingSettingsModel: IAccessRightCoreFilter,
        isChecklistShared: boolean,
    ): ValidationErrors {
        return this.fiAccessRightHelperService.validateAccessRightsCoreFilters(sharingSettingsModel, {
            isShared: isChecklistShared,
            skipRolesCheck: true,
        });
    }

    protected validateSectionFn(sectionId: FiTodoListEditorSections): ValidationErrors {
        if (sectionId === FiTodoListEditorSections.TodoItems) {
            return this.validateCheckpointsFn(this.todoListItem().Sections);
        }
        if (sectionId === FiTodoListEditorSections.SharingSettings) {
            return this.validateSharingSettingsFn(
                this.todoListItem().AccessRightFilterCollection.AccessRightCoreFilter,
                this.todoListItem().IsShared,
            );
        }
    }

    private openSharingSettingsDialog(): void {
        this.fiTodoHelperService.openSharingSettingsDialog();
    }

    protected isTodoItemsSection(sectionId: FiTodoListEditorSections): boolean {
        return sectionId === FiTodoListEditorSections.TodoItems;
    }

    protected isSharingSettingsSection(sectionId: FiTodoListEditorSections): boolean {
        return sectionId === FiTodoListEditorSections.SharingSettings;
    }

    protected isSectionTooltipShown(sectionId: FiTodoListEditorSections): boolean {
        return this.isCopyMode() && this.isSharingSettingsSection(sectionId) && this.isSharingSettingsEqualToInitial();
    }

    private isSharingSettingsEqualToInitial(): boolean {
        return (
            !!this.todoListItem().IsShared &&
            isEqual(this.todoListItem().AccessRightFilterCollection, AccessRightFilterCollectionModel.getNewInstance())
        );
    }

    protected getSharingSelectionTooltipText(): string {
        return this.appTextService.get(this.isCopyMode() ? 'error.sharing_settings_reminder' : '');
    }

    private navigateToSectionsEdit(): void {
        this.saveChangesWarningService.stop();
        this.fiTodoHelperService.navigateToSectionsEditor();
    }

    protected readonly editorSections: Readonly<ISectionCardData[]> =
        this.fiTodoHelperService.TodoListEditorSections.map((section) => {
            return {
                ...section,
                onClick:
                    section.id === FiTodoListEditorSections.TodoItems
                        ? () => this.navigateToSectionsEdit()
                        : () => this.openSharingSettingsDialog(),
            };
        });

    protected readonly ObjectTypes = ObjectTypes;
}
